/**
 * OfficeManagerReport — Phase 10A
 *
 * Full Office Manager report with:
 *  - Variant toggle (daily/weekly)
 *  - Date picker
 *  - Section components for all 6 sections
 *  - PDF / CSV download buttons
 *  - Auto-refresh every 5 min (daily variant)
 *
 * data-testid: candeza-reports-operations-* (renamed in Phase 10A.6)
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from '@/components/common';
import { useAuth } from '@/context';
import {
  useOfficeManagerReport,
  exportOfficeManagerReport,
} from '@/api/reports';
import { TodayAtAGlance } from './TodayAtAGlance';
import { ActionItems } from './ActionItems';
import { TodaysSchedule } from './TodaysSchedule';
import { RecentActivity } from './RecentActivity';
import { TeacherWorkload } from './TeacherWorkload';

// ── Styled components ─────────────────────────────────────────────────────────

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
`;

const VariantToggle = styled.div`
  display: flex;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const VariantBtn = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.md}`};
  border: none;
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.surface)};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.text)};
  transition: all 0.2s;

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.primary : theme.colors.border};
  }
`;

const DateInput = styled.input`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.surface};
`;

const ExportRow = styled.div`
  margin-left: auto;
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const SectionWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  padding-left: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Meta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const CacheBadge = styled.span`
  background: #dcfce7;
  color: #166534;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 10px;
`;

const ErrorBadge = styled.span`
  background: #fee2e2;
  color: #b91c1c;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 10px;
`;

const ErrorBanner = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  color: #b91c1c;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

// ── Component ─────────────────────────────────────────────────────────────────

export const OfficeManagerReport: React.FC = () => {
  const { getToken } = useAuth() as { getToken?: () => Promise<string> };
  const [variant, setVariant] = useState<'daily' | 'weekly'>('daily');
  const [date, setDate] = useState<string>(''); // '' = today
  const [exporting, setExporting] = useState<'pdf' | 'csv' | null>(null);

  const { data, isLoading, isError, dataUpdatedAt } = useOfficeManagerReport({
    variant,
    date: date || undefined,
  });

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  const handleExport = async (format: 'pdf' | 'csv') => {
    setExporting(format);
    try {
      const token = getToken ? await getToken() : '';
      await exportOfficeManagerReport({
        variant,
        date: date || undefined,
        format,
        token,
      });
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div data-testid="candeza-reports-operations-page">
      {/* ── Controls ─────────────────────────────────────────────────── */}
      <Controls data-testid="candeza-reports-operations-controls">
        <VariantToggle>
          <VariantBtn
            $active={variant === 'daily'}
            onClick={() => setVariant('daily')}
            data-testid="candeza-reports-operations-variant-daily"
          >
            Daily
          </VariantBtn>
          <VariantBtn
            $active={variant === 'weekly'}
            onClick={() => setVariant('weekly')}
            data-testid="candeza-reports-operations-variant-weekly"
          >
            Weekly
          </VariantBtn>
        </VariantToggle>

        <DateInput
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="candeza-reports-operations-date-picker"
          max={new Date().toISOString().split('T')[0]}
        />

        <ExportRow>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('csv')}
            disabled={exporting !== null || isLoading}
            data-testid="candeza-reports-operations-export-csv"
          >
            {exporting === 'csv' ? 'Exporting…' : '↓ CSV'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('pdf')}
            disabled={exporting !== null || isLoading}
            data-testid="candeza-reports-operations-export-pdf"
          >
            {exporting === 'pdf' ? 'Exporting…' : '↓ PDF'}
          </Button>
        </ExportRow>
      </Controls>

      {/* ── Meta bar ─────────────────────────────────────────────────── */}
      {lastUpdated && (
        <Meta data-testid="candeza-reports-operations-meta">
          <span>Last updated: {lastUpdated}</span>
          {variant === 'daily' && <span>· Auto-refreshes every 5 min</span>}
          {data?.cache_hit && <CacheBadge>Cached</CacheBadge>}
          {data?.errors.length ? (
            <ErrorBadge>{data.errors.length} section(s) failed</ErrorBadge>
          ) : null}
        </Meta>
      )}

      {/* ── Error state ───────────────────────────────────────────────── */}
      {isError && (
        <ErrorBanner data-testid="candeza-reports-operations-error">
          Failed to load report. Check your connection and try again.
        </ErrorBanner>
      )}

      {/* ── Section 1: Today at a Glance ─────────────────────────────── */}
      <SectionWrapper>
        <SectionTitle>Today at a Glance</SectionTitle>
        {data && (
          <TodayAtAGlance
            snapshot={data.today_at_a_glance}
            loading={isLoading}
          />
        )}
        {isLoading && (
          <TodayAtAGlance
            snapshot={{
              sessions: { total: 0, confirmed: 0, cancelled: 0, completed: 0, in_progress: 0 },
              bookings_created_today: 0,
              payments_today_count: 0,
              payments_today_total: 0,
              new_students_today: 0,
              trial_inquiries_pending: 0,
              sessions_awaiting_attendance: 0,
            }}
            loading={true}
          />
        )}
      </SectionWrapper>

      {/* ── Section 2: Action Items ───────────────────────────────────── */}
      <SectionWrapper>
        <SectionTitle>⚡ Action Items</SectionTitle>
        {data && <ActionItems section={data.action_items} />}
        {isLoading && !data && (
          <div data-testid="candeza-reports-operations-actions-loading">Loading action items…</div>
        )}
      </SectionWrapper>

      {/* ── Section 3: Today's Schedule ──────────────────────────────── */}
      <SectionWrapper>
        <SectionTitle>Today's Schedule</SectionTitle>
        <TodaysSchedule
          sessions={data?.today_schedule ?? []}
          loading={isLoading && !data}
        />
      </SectionWrapper>

      {/* ── Section 4: Recent Activity ───────────────────────────────── */}
      <SectionWrapper>
        <SectionTitle>Recent Activity</SectionTitle>
        <RecentActivity
          entries={data?.recent_activity ?? []}
          loading={isLoading && !data}
        />
      </SectionWrapper>

      {/* ── Section 6: Teacher Workload (weekly) ─────────────────────── */}
      {variant === 'weekly' && (
        <SectionWrapper>
          <SectionTitle>Teacher Workload</SectionTitle>
          <TeacherWorkload
            entries={data?.teacher_workload ?? []}
            loading={isLoading && !data}
          />
        </SectionWrapper>
      )}
    </div>
  );
};

